import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { legalApi } from '../api/client';
import { usePageTitle } from '../hooks/usePageTitle';

const ALLOWED_TAGS = new Set([
  'a', 'b', 'blockquote', 'br', 'em', 'h2', 'h3', 'i', 'li', 'ol', 'p', 'strong', 'ul',
]);
const BLOCKED_TAGS = new Set(['iframe', 'object', 'script', 'style', 'svg', 'template']);
const ALLOWED_LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);

function sanitizeLegalHtml(html) {
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const sanitized = document.createElement('div');

  const copyNode = (node, parent) => {
    if (node.nodeType === Node.TEXT_NODE) {
      parent.appendChild(document.createTextNode(node.textContent || ''));
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const tagName = node.tagName.toLowerCase();
    if (BLOCKED_TAGS.has(tagName)) return;

    if (!ALLOWED_TAGS.has(tagName)) {
      node.childNodes.forEach((child) => copyNode(child, parent));
      return;
    }

    const element = document.createElement(tagName);
    if (tagName === 'a') {
      const href = node.getAttribute('href')?.trim();
      if (href) {
        try {
          const url = new URL(href, window.location.origin);
          if (ALLOWED_LINK_PROTOCOLS.has(url.protocol)) element.setAttribute('href', href);
        } catch {
          // Ignore invalid link targets.
        }
      }
    }
    node.childNodes.forEach((child) => copyNode(child, element));
    parent.appendChild(element);
  };

  parsed.body.childNodes.forEach((node) => copyNode(node, sanitized));
  return sanitized.innerHTML;
}

export default function LegalPage({ slug }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['legal', slug],
    queryFn: async () => (await legalApi.get(slug)).data.data,
    staleTime: 5 * 60 * 1000,
  });
  const content = React.useMemo(() => sanitizeLegalHtml(data?.Content || ''), [data?.Content]);
  usePageTitle(`${data?.Title || (slug === 'terms' ? 'Terms and Conditions' : 'Privacy Policy')} · Kast`);

  return (
    <main className="mx-auto max-w-3xl px-5 py-10 sm:px-8 sm:py-14">
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {isError && <p className="text-sm text-muted-foreground">This page is unavailable right now.</p>}
      {data && <article>
        <h1 className="text-3xl font-bold tracking-tight text-foreground">{data.Title}</h1>
        <p className="mt-2 text-xs text-muted-foreground">Last updated {new Date(data.LastUpdated).toLocaleDateString()}</p>
        <div
          className="legal-content mt-8 space-y-4 text-sm leading-7 text-muted-foreground [&_a]:text-primary [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-primary/40 [&_blockquote]:pl-4 [&_h2]:mt-8 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-foreground [&_h3]:mt-6 [&_h3]:font-semibold [&_h3]:text-foreground [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </article>}
    </main>
  );
}
